/**
 * NotificationsBell — header IconButton + Badge. Subscribes via
 * notificationsService.subscribe; keeps the last 5 notifications in
 * component-local state (deliberately no global store). Mark-as-read is
 * optimistic (revert + snackbar on failure). Types in TOAST_ON_ARRIVAL
 * also fire a transient toast.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, IconButton, Popover, Tooltip } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { colors, designTokens } from '../../theme/theme';
import notificationsService, {
  NotificationPayload,
} from '../../services/notificationsService';
import httpClient from '../../utils/httpClient';
import AppSnackbar from '../shared/AppSnackbar';
import NotificationsList, { NotificationItem } from './NotificationsList';

const MAX_VISIBLE = 5;

// Time-sensitive types that also toast on arrival; keep in sync with the
// deep-link map in NotificationsList.
const TOAST_ON_ARRIVAL = new Set(['attendance_otp', 'voucher_unlock']);

function payloadToItem(p: NotificationPayload): NotificationItem | null {
  // Skip protocol envelopes that aren't user-facing notifications.
  // The backend ws handler emits `pong` heartbeats (notifications.py:518-522)
  // and the realtime manager wraps real notifications in `type: 'notification'`
  // (realtime.py:106-111). We render anything that ISN'T 'pong' / 'broadcast'
  // metadata — but we still need a stable ID + a notification-type to drive
  // deep-linking, so generate one if absent.
  const envelopeType = (typeof p?.type === 'string' ? p.type : '') as string;
  if (envelopeType === 'pong') return null;

  // The realtime manager spreads the inner payload alongside the envelope
  // (`type: 'notification'`, then `...notification_data`). The inner payload
  // typically has its OWN type-like key under `category` or `template_name`.
  // We fall back across a few common shapes.
  const innerType = (
    (typeof p.notification_type === 'string' && p.notification_type) ||
    (typeof p.template_name === 'string' && p.template_name) ||
    (envelopeType !== 'notification' ? envelopeType : '') ||
    (typeof p.category === 'string' && p.category) ||
    'unknown'
  ) as string;

  const id =
    (typeof p.id === 'string' && p.id) ||
    (typeof p.notification_id === 'string' && (p.notification_id as string)) ||
    `wsnotif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const title =
    (typeof p.title === 'string' && p.title) ||
    (typeof p.subject === 'string' && (p.subject as string)) ||
    'Notification';

  const body =
    (typeof p.body === 'string' && p.body) ||
    (typeof p.message === 'string' && p.message) ||
    '';

  const createdAt =
    (typeof p.created_at === 'string' && p.created_at) ||
    (typeof p.timestamp === 'string' && p.timestamp) ||
    new Date().toISOString();

  return {
    id,
    type: innerType,
    title,
    body,
    data: (p.data && typeof p.data === 'object' ? (p.data as Record<string, unknown>) : undefined),
    created_at: createdAt,
    is_read: false,
  };
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'info' | 'error' | 'success' }>(
    { open: false, message: '', severity: 'info' },
  );
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const unreadCount = useMemo(() => items.filter(i => !i.is_read).length, [items]);

  const handleIncoming = useCallback((payload: NotificationPayload) => {
    const item = payloadToItem(payload);
    if (!item) return;
    setItems(prev => {
      // Dedupe by ID — server may resend on reconnect; we don't want doubles.
      if (prev.some(i => i.id === item.id)) return prev;
      // Newest first; cap to MAX_VISIBLE * 2 so the popover always has a
      // healthy list even after a few reads.
      const next = [item, ...prev].slice(0, MAX_VISIBLE * 2);
      return next;
    });
    if (TOAST_ON_ARRIVAL.has(item.type)) {
      setSnackbar({ open: true, message: item.title, severity: 'info' });
    }
  }, []);

  // Subscribe / unsubscribe with auth lifecycle. We store the unsubscribe in a
  // ref (not state) so it survives re-renders without re-triggering the effect.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      // Logout: tear down any live subscription.
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      setItems([]);
      return;
    }
    if (unsubscribeRef.current) return; // already subscribed
    unsubscribeRef.current = notificationsService.subscribe(user.id, handleIncoming);
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, handleIncoming]);

  const handleMarkRead = useCallback(async (id: string) => {
    // Optimistic update.
    let previousState: NotificationItem[] = [];
    setItems(prev => {
      previousState = prev;
      return prev.map(i => (i.id === id ? { ...i, is_read: true } : i));
    });
    try {
      await httpClient.put(`/notifications/${id}/read`);
    } catch (err) {
      // Revert + surface error.
      console.warn('[NotificationsBell] mark-read failed', err);
      setItems(previousState);
      setSnackbar({ open: true, message: 'Could not mark notification as read', severity: 'error' });
    }
  }, []);

  const handleNavigate = useCallback((path: string) => {
    setAnchorEl(null);
    navigate(path);
  }, [navigate]);

  const handleDismiss = useCallback((id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  if (!isAuthenticated) return null;

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          sx={{
            color: colors.neutral[500],
            transition: designTokens.transition.fast,
            '&:hover': {
              color: colors.primary.main,
              bgcolor: 'rgba(255,255,255,0.06)',
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            max={9}
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: colors.primary.main,
                color: '#FFFFFF',
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 18,
                height: 18,
                boxShadow: `0 0 0 2px ${colors.background.default}`,
              },
            }}
          >
            <NotificationsIcon sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 360,
              maxWidth: '90vw',
              borderRadius: 3,
              border: `1px solid ${designTokens.glass.border}`,
              backgroundColor: colors.background.surface,
              backdropFilter: designTokens.glass.blur,
              backgroundImage: 'none',
              boxShadow: designTokens.shadow.lg,
              overflow: 'hidden',
            },
          },
        }}
      >
        <NotificationsList
          items={items.slice(0, MAX_VISIBLE)}
          onNavigate={handleNavigate}
          onMarkRead={handleMarkRead}
          onDismiss={handleDismiss}
          onSeeAll={() => {
            setAnchorEl(null);
            navigate('/notifications');
          }}
        />
      </Popover>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        duration={4000}
      />
    </>
  );
}
